import React from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { convertGradeToNumber } from '../utils/climbing-grades';

const WallDetailGraph = ({ proposedGrades = [] }) => {
  const theme = useTheme();

  const gradeToNumber = (grade) => convertGradeToNumber(grade) ?? 0;

  // Funzione per ottenere tutti i gradi unici per le etichette Y
  const getUniqueGrades = (grades) => {
    const uniqueGrades = [...new Set(grades.map(g => g.grade).filter(Boolean))];
    return uniqueGrades.sort((a, b) => gradeToNumber(a) - gradeToNumber(b));
  };

  // Trasforma i dati per il grafico
  const rawData = proposedGrades
    .map((grade, index) => ({
      date: dayjs(grade.updatedAt).format('DD-MMM-YY'),
      fullDate: grade.updatedAt,
      dateNumber: new Date(grade.updatedAt).getTime(), // Valore numerico per l'asse X
      grade: grade.grade,
      gradeNumber: gradeToNumber(grade.grade),
      rating: grade.rating,
      user: grade.userId ? `${grade.userId.name} ${grade.userId.surname}` : 'N/A',
      notes: grade.notes,
      timestamp: new Date(grade.updatedAt).getTime(),
    }))
    .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));

  // Usa tutti i dati senza raggruppamento - un punto per ogni grado proposto
  const chartData = rawData.map((item, index) => ({
    ...item,
    xOffset: 0,
    isMultiple: false, // Ogni punto rappresenta un singolo contributo
  }));

  const uniqueGrades = getUniqueGrades(rawData);

  // Se non ci sono dati, mostra un messaggio
  if (chartData.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Gradi Proposti
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nessun grado proposto disponibile per visualizzare il grafico.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Gradi Proposti
      </Typography>
      <Box sx={{ width: '100%', height: 400 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="dateNumber"
              type="number"
              scale="time"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={(value) => {
                return dayjs(value).format('DD-MMM-YY');
              }}
              domain={['dataMin', 'dataMax']}
            />
            <YAxis 
              dataKey="gradeNumber"
              stroke={theme.palette.text.secondary}
              fontSize={12}
              // label={{ value: 'Grado Proposto', angle: -90, position: 'insideLeft' }}
              domain={['dataMin - 0.5', 'dataMax + 0.5']}
              tickFormatter={(value) => {
                // Trova il grado originale corrispondente al valore numerico
                const originalGrade = uniqueGrades.find(grade => 
                  Math.abs(gradeToNumber(grade) - value) < 0.1
                );
                return originalGrade || value.toString();
              }}
              ticks={uniqueGrades.map(grade => gradeToNumber(grade))}
            />
            <Scatter
              data={chartData}
              fill={theme.palette.primary.main}
              shape={(props) => {
                const { cx, cy, payload } = props;
                if (!payload) return null;
                
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={theme.palette.primary.main}
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                  />
                );
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Box>
      {/* <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Ogni punto rappresenta un grado proposto. I punti sovrapposti indicano più gradi proposti nello stesso giorno.
      </Typography> */}
    </Paper>
  );
};

export default WallDetailGraph;
